import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Download, FileText, Plus } from "lucide-react";
import { Card, Button } from "@/design-system/components/ui";
import { Table, THead, TBody, TR, TH, TD } from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import { useAuth } from "@/hooks/useAuth";
import { useSpvPoolMath } from "@/hooks/capitalMarkets/useSpvPools";
import { useInvestorCommitments, useInvestorList } from "@/hooks/capitalMarkets/useInvestors";
import {
  useEntityNameMap,
  usePersonNameMap,
  resolveInvestorName,
} from "@/hooks/capitalMarkets/useCounterpartyNames";
import type { SpvPoolDto } from "@/types/spvPool";
import {
  INVESTOR_COMMITMENT_STATUSES,
  type InvestorMaskedDto,
} from "@/types/investors";
import { CommitmentStatusBadge, VerificationChip } from "../../shared/components";
import { downloadCsv, formatCurrency, humanizeStatus } from "../../shared/format";
import { InviteInvestorWizard } from "../components/InviteInvestorWizard";

export function InvestorsTab({ pool }: { pool: SpvPoolDto }) {
  const { hasPermission } = useAuth();
  const canInvite =
    hasPermission("SpvPool.Build") && hasPermission("Investor.Manage");

  const [trancheFilter, setTrancheFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showWizard, setShowWizard] = useState(false);

  const mathQuery = useSpvPoolMath(pool.id);
  const commitmentsQuery = useInvestorCommitments({
    spvPoolId: pool.id,
    pageSize: 200,
  });
  const investorsQuery = useInvestorList({ pageSize: 200 });
  const { map: personNames } = usePersonNameMap();
  const { map: entityNames } = useEntityNameMap();

  const commitments = useMemo(
    () => commitmentsQuery.data?.data ?? [],
    [commitmentsQuery.data],
  );
  const tranches = mathQuery.data?.perTrancheFill ?? [];

  const investorById = useMemo(() => {
    const m = new Map<string, InvestorMaskedDto>();
    for (const i of investorsQuery.data?.data ?? []) m.set(i.id, i);
    return m;
  }, [investorsQuery.data]);

  const trancheCodeById = useMemo(() => {
    const m = new Map<string, string>();
    for (const t of tranches) m.set(t.poolTrancheId, t.trancheCode);
    return m;
  }, [tranches]);

  // Funded total per tranche, derived from this pool's commitments.
  const fundedByTranche = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of commitments) {
      m.set(c.poolTrancheId, (m.get(c.poolTrancheId) ?? 0) + c.fundedAmount);
    }
    return m;
  }, [commitments]);

  const rows = useMemo(
    () =>
      commitments.filter((c) => {
        if (trancheFilter && c.poolTrancheId !== trancheFilter) return false;
        if (statusFilter && c.status !== statusFilter) return false;
        return true;
      }),
    [commitments, trancheFilter, statusFilter],
  );

  const investorName = (investorId: string) => {
    const inv = investorById.get(investorId);
    if (!inv) return "Investor";
    return resolveInvestorName(inv.personId, inv.entityId, personNames, entityNames);
  };

  const handleExport = () => {
    const header = [
      "Investor",
      "Tranche",
      "Committed",
      "Funded",
      "Status",
      "Accreditation",
      "KYC",
    ];
    const csv = rows.map((c) => {
      const inv = investorById.get(c.investorId);
      return [
        investorName(c.investorId),
        trancheCodeById.get(c.poolTrancheId) ?? "",
        String(c.committedAmount),
        String(c.fundedAmount),
        humanizeStatus(c.status),
        inv?.accreditationVerifiedAt ? "Verified" : "Pending",
        inv?.amlKycVerifiedAt ? "Verified" : "Pending",
      ];
    });
    downloadCsv(`pool-${pool.poolName}-investors.csv`, [header, ...csv]);
  };

  return (
    <div className="flex flex-col gap-space-4">
      <Card>
        <div className="flex items-center justify-between mb-space-3">
          <h3 className="font-display font-bold text-h3 m-0">
            Tranche fill
          </h3>
          {canInvite && (
            <Button size="sm" onClick={() => setShowWizard(true)}>
              <Plus size={14} aria-hidden /> Invite Investor
            </Button>
          )}
        </div>
        {tranches.length === 0 ? (
          <p className="text-small text-gray-mid italic m-0">
            No tranches configured for this pool yet.
          </p>
        ) : (
          <div className="flex flex-col gap-space-3">
            {tranches.map((t) => {
              const funded = fundedByTranche.get(t.poolTrancheId) ?? 0;
              const target = t.targetAmount || 1;
              const committedPct = Math.min(
                100,
                (t.committedAmount / target) * 100,
              );
              const fundedPct = Math.min(100, (funded / target) * 100);
              return (
                <div key={t.poolTrancheId}>
                  <div className="flex items-center justify-between text-caption mb-space-1">
                    <span className="font-semibold">Tier {t.trancheCode}</span>
                    <span className="text-gray-mid">
                      {formatCurrency(t.committedAmount)} committed ·{" "}
                      {formatCurrency(funded)} funded ·{" "}
                      {formatCurrency(t.targetAmount)} target
                    </span>
                  </div>
                  {/* Target track (gray) with committed (gold) + funded
                      (gold-shadow) overlays. Widths are data-driven. */}
                  <div className="relative h-5 rounded-sm bg-border-light overflow-hidden">
                    <div
                      className="absolute inset-y-0 left-0 bg-gold"
                      style={{ width: `${committedPct}%` }}
                    />
                    <div
                      className="absolute inset-y-0 left-0 bg-gold-shadow"
                      style={{ width: `${fundedPct}%` }}
                    />
                  </div>
                </div>
              );
            })}
            <div className="flex gap-space-4 text-caption text-gray-mid">
              <LegendSwatch className="bg-border-light" label="Target" />
              <LegendSwatch className="bg-gold" label="Committed" />
              <LegendSwatch className="bg-gold-shadow" label="Funded" />
            </div>
          </div>
        )}
      </Card>

      <Card>
        <div className="flex flex-wrap items-center justify-between gap-space-3 mb-space-3">
          <h3 className="font-display font-bold text-h3 m-0">
            Investor commitments
          </h3>
          <div className="flex items-center gap-space-2">
            <Select
              aria-label="Filter by tranche"
              className="w-40"
              value={trancheFilter}
              onChange={(e) => setTrancheFilter(e.target.value)}
            >
              <option value="">All tranches</option>
              {tranches.map((t) => (
                <option key={t.poolTrancheId} value={t.poolTrancheId}>
                  Tier {t.trancheCode}
                </option>
              ))}
            </Select>
            <Select
              aria-label="Filter by status"
              className="w-40"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All statuses</option>
              {INVESTOR_COMMITMENT_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {humanizeStatus(s)}
                </option>
              ))}
            </Select>
            <Button variant="secondary" size="sm" onClick={handleExport}>
              <Download size={14} strokeWidth={1.5} aria-hidden /> Export CSV
            </Button>
          </div>
        </div>
        {commitmentsQuery.isLoading ? (
          <p className="text-small text-gray-mid italic m-0">
            Loading commitments…
          </p>
        ) : rows.length === 0 ? (
          <p className="text-small text-gray-mid italic m-0">
            No investor commitments on this pool yet. Use Invite Investor to add
            the first one.
          </p>
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>Investor</TH>
                <TH>Tranche</TH>
                <TH>Committed</TH>
                <TH>Funded</TH>
                <TH>Status</TH>
                <TH>Sub. Agreement</TH>
                <TH>Accreditation</TH>
                <TH>KYC</TH>
              </TR>
            </THead>
            <TBody>
              {rows.map((c) => {
                const inv = investorById.get(c.investorId);
                return (
                  <TR key={c.id}>
                    <TD>
                      <Link
                        to={`/capital-markets/investors/${c.investorId}`}
                        className="font-semibold hover:text-gold-shadow"
                      >
                        {investorName(c.investorId)}
                      </Link>
                    </TD>
                    <TD>{trancheCodeById.get(c.poolTrancheId) ?? "—"}</TD>
                    <TD>{formatCurrency(c.committedAmount)}</TD>
                    <TD>{formatCurrency(c.fundedAmount)}</TD>
                    <TD>
                      <CommitmentStatusBadge status={c.status} />
                    </TD>
                    <TD>
                      {c.subscriptionAgreementDocumentId ? (
                        <FileText
                          size={16}
                          className="text-gold-shadow"
                          aria-label="Subscription agreement on file"
                        />
                      ) : (
                        <span className="text-gray-mid">—</span>
                      )}
                    </TD>
                    <TD>
                      <VerificationChip
                        verifiedAt={inv?.accreditationVerifiedAt}
                      />
                    </TD>
                    <TD>
                      <VerificationChip verifiedAt={inv?.amlKycVerifiedAt} />
                    </TD>
                  </TR>
                );
              })}
            </TBody>
          </Table>
        )}
      </Card>

      {showWizard && (
        <InviteInvestorWizard
          poolId={pool.id}
          poolName={pool.poolName}
          onClose={() => setShowWizard(false)}
          onComplete={() => {
            commitmentsQuery.refetch();
            mathQuery.refetch();
          }}
        />
      )}
    </div>
  );
}

function LegendSwatch({
  className,
  label,
}: {
  className: string;
  label: string;
}) {
  return (
    <span className="flex items-center gap-space-1">
      <span className={`inline-block h-3 w-3 rounded-sm ${className}`} />
      {label}
    </span>
  );
}
